import React, { FC, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { OUTLINE_BASE_PADDING, OUTLINE_INDENT } from '../constants';
import { OutlineState } from '../hooks/useOutline';
import { OutlineEntry } from '../types';
import styles from '../PdfTileViewer.module.scss';

interface OutlineNodeProps {
    item: OutlineEntry;
    level: number;
    onSelect: (pageIndex: number) => void;
}

const OutlineNode: FC<OutlineNodeProps> = ({ item, level, onSelect }) => {
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const hasChildren = item.children.length > 0;
    const caret = isCollapsed ? '▸' : '▾';

    const select = (): void => {
        if (item.pageIndex !== null && item.pageIndex !== undefined) {
            onSelect(item.pageIndex);
        }
    };

    const toggle = (event: React.MouseEvent): void => {
        event.stopPropagation();
        setIsCollapsed((value) => !value);
    };

    return (
        <li>
            <div
                className={styles['outline-row']}
                style={{ paddingLeft: OUTLINE_BASE_PADDING + level * OUTLINE_INDENT }}
                role="button"
                tabIndex={0}
                onClick={select}
                onKeyDown={(event) => event.key === 'Enter' && select()}
            >
                <span className={styles['outline-caret']} onClick={toggle} role="presentation">
                    {hasChildren ? caret : ''}
                </span>
                <span>{item.title || t('viewer.tileViewer.untitled')}</span>
            </div>
            {hasChildren && !isCollapsed && (
                <ul>
                    {item.children.map((child) => (
                        <OutlineNode key={child.id} item={child} level={level + 1} onSelect={onSelect} />
                    ))}
                </ul>
            )}
        </li>
    );
};

interface OutlineProps {
    state: OutlineState;
    onSelect: (pageIndex: number) => void;
}

const Outline: FC<OutlineProps> = ({ state, onSelect }) => {
    const { t } = useTranslation();

    if (state.status === 'ready' && state.items.length) {
        return (
            <ul>
                {state.items.map((item) => (
                    <OutlineNode key={item.id} item={item} level={0} onSelect={onSelect} />
                ))}
            </ul>
        );
    }
    let note = t('viewer.tileViewer.loading');
    if (state.status === 'failed') {
        note = t('viewer.tileViewer.outlineFailed');
    } else if (state.status === 'ready') {
        note = t('viewer.tileViewer.noOutline');
    }
    return <div className={styles['outline-note']}>{note}</div>;
};

export default Outline;
